import React, { useEffect, useState, useMemo } from "react";
import { Smartphone, Share2, Plus, X, Download } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

/**
 * Big, friendly "Add to home screen" CTA for the Landing page.
 *
 * Behaviour:
 *  - Hides automatically once the app is already installed (display-mode
 *    standalone), so existing PWA users don't see clutter.
 *  - On Android / Chrome / Edge: listens for the `beforeinstallprompt`
 *    event and shows a one-tap install button that fires the native
 *    install prompt.
 *  - On iOS Safari: there's no programmatic install, so we render a clear
 *    visual guide (Share icon → "Add to Home Screen") instead.
 *  - User can dismiss; we remember dismissal in localStorage for 14 days.
 *
 * Designed to look inviting, not naggy — the dismiss is always one tap.
 */
const DISMISSED_KEY = "ccuk_pwa_install_dismissed_at";
const DISMISS_TTL_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator?.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

export default function InstallPwaCard() {
  const [deferred, setDeferred] = useState(null); // beforeinstallprompt
  const [installed, setInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [iosGuideOpen, setIosGuideOpen] = useState(false);

  // QR code value — pretty + scannable. Prefers REACT_APP_PUBLIC_URL so it
  // automatically points at camp-compass.uk in production.
  const qrUrl = useMemo(() => {
    const fromEnv = (process.env.REACT_APP_PUBLIC_URL || "").trim();
    if (fromEnv) return fromEnv.replace(/\/$/, "");
    if (typeof window !== "undefined") return window.location.origin;
    return "https://camp-compass.uk";
  }, []);

  // Check if the user has dismissed within the TTL
  useEffect(() => {
    try {
      const at = parseInt(localStorage.getItem(DISMISSED_KEY) || "0", 10);
      if (at && Date.now() - at < DISMISS_TTL_MS) setDismissed(true);
    } catch { /* localStorage may be disabled */ }
    if (isStandalone()) setInstalled(true);
  }, []);

  // Capture the install prompt for Android / desktop Chrome
  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISSED_KEY, String(Date.now())); } catch { /* ignore */ }
    setDismissed(true);
  };

  const onInstall = async () => {
    if (deferred) {
      deferred.prompt();
      try { await deferred.userChoice; } catch { /* user closed */ }
      setDeferred(null);
      return;
    }
    if (isIOS()) {
      setIosGuideOpen(true);
      return;
    }
    // Desktop / unsupported browsers: short fallback guide
    setIosGuideOpen(true);
  };

  // Hide entirely if already installed or recently dismissed
  if (installed || dismissed) return null;

  return (
    <section className="py-12 bg-[#111] text-white relative overflow-hidden" data-testid="install-pwa-card">
      {/* Sparkle accent */}
      <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-[#ff4500] opacity-20 blur-3xl" />
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10 relative">
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          className="absolute top-3 right-3 sm:top-0 sm:right-0 p-2 text-white/50 hover:text-white transition-colors"
          data-testid="install-pwa-dismiss"
        >
          <X className="w-4 h-4" strokeWidth={2} />
        </button>

        <div className="grid lg:grid-cols-[auto_1fr_auto] items-center gap-6 lg:gap-10">
          {/* Phone mock-icon */}
          <div className="flex-shrink-0 inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#ff4500]/15 border border-[#ff4500]/30">
            <Smartphone className="w-8 h-8 text-[#ff4500]" strokeWidth={1.6} />
          </div>

          {/* Copy */}
          <div>
            <div className="eyebrow text-[#ff4500] mb-1.5 flex items-center gap-1.5">
              <Download className="w-3 h-3" /> One-tap save · No app store
            </div>
            <h3 className="font-display text-2xl lg:text-3xl tracking-tight mb-2">
              Pop CampCompass on your phone — like an app
            </h3>
            <p className="text-sm text-white/85 leading-relaxed max-w-xl">
              Tap the orange button. CampCompass will sit on your home screen next to your other apps —
              ready in one tap, no store, no sign-up, no faff. Brilliant for trips: works even when
              you're miles from a signal.
            </p>
          </div>

          {/* CTA */}
          <div className="flex flex-col gap-2 lg:items-end">
            <button
              onClick={onInstall}
              className="bg-[#ff4500] hover:bg-[#e63d00] text-white px-6 py-3 mono uppercase tracking-wider text-xs font-bold inline-flex items-center gap-2 shadow-[0_4px_0_0_#fff] hover:shadow-[0_2px_0_0_#fff] hover:translate-y-[2px] transition-all"
              data-testid="install-pwa-btn"
            >
              <Plus className="w-4 h-4" strokeWidth={3} /> Save to my phone
            </button>
            <span className="text-[11px] mono text-white/60 lg:text-right">Free · takes 5 seconds · no card needed</span>

            {/* Desktop-only: QR code so a desktop visitor can install on their phone in 2 taps */}
            <div className="hidden lg:flex flex-col items-end mt-4 pt-4 border-t border-white/15 w-full">
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="eyebrow text-[#ff4500] mb-1">On a desktop?</div>
                  <div className="text-[11px] mono text-white/65 leading-snug max-w-[180px]">
                    Scan with your phone camera to install on the go
                  </div>
                </div>
                <div className="bg-white p-2.5" data-testid="install-pwa-qr">
                  <QRCodeSVG
                    value={qrUrl}
                    size={86}
                    level="M"
                    bgColor="#ffffff"
                    fgColor="#111111"
                    aria-label={`QR code linking to ${qrUrl}`}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* iOS / fallback step-by-step guide */}
      {iosGuideOpen && (
        <div
          className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setIosGuideOpen(false)}
          data-testid="install-pwa-ios-modal"
        >
          <div
            className="bg-white text-[#111] max-w-md w-full p-6 border-2 border-[#ff4500]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="eyebrow text-[#ff4500]">{isIOS() ? "iPhone — 3 quick taps" : "Save to home screen"}</div>
                <h4 className="font-display text-2xl mt-1">Just 3 taps</h4>
              </div>
              <button onClick={() => setIosGuideOpen(false)} className="p-2 hover:bg-[#fafafa]" data-testid="install-pwa-ios-close" aria-label="Close">
                <X className="w-4 h-4" />
              </button>
            </div>

            <ol className="space-y-3 mt-4 mb-2">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#ff4500] text-white inline-flex items-center justify-center mono text-xs font-bold">1</span>
                <span className="text-sm leading-relaxed">
                  Tap the <strong className="inline-flex items-center gap-1 mx-0.5"><Share2 className="w-3.5 h-3.5 inline" /> Share</strong> button
                  {isIOS() ? " at the bottom of your screen" : " in your browser menu"} (looks like a square with an arrow pointing up).
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#ff4500] text-white inline-flex items-center justify-center mono text-xs font-bold">2</span>
                <span className="text-sm leading-relaxed">
                  Scroll down and tap <strong>"Add to Home Screen"</strong>.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#ff4500] text-white inline-flex items-center justify-center mono text-xs font-bold">3</span>
                <span className="text-sm leading-relaxed">
                  Tap <strong>Add</strong> in the top-right corner. That's it — CampCompass is now on your home screen.
                </span>
              </li>
            </ol>

            <p className="text-xs text-[#666] mt-4 leading-snug border-t hairline pt-3">
              💡 Open it from your home screen and it works just like a regular app — no browser bars, no
              fuss. The campsite info you've already viewed will even work without signal.
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
